import { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { progressAPI } from "../services/api";
import {
  Box, AppBar, Toolbar, Typography, Button, Card, CardContent,
  Grid, Chip, Avatar, CircularProgress, Alert, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment,
  Collapse, IconButton
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import LockIcon from "@mui/icons-material/Lock";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DownloadIcon from "@mui/icons-material/Download";

export default function SupervisorDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPath, setFilterPath] = useState("all");
  const [expandedAgent, setExpandedAgent] = useState(null);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      const teamId = user?.team_id;
      if (!teamId) { setError("You are not assigned to a team. Please contact your administrator."); setLoading(false); return; }
      const res = await progressAPI.getTeamDashboard(teamId);
      setDashboard(res.data);
    } catch { setError("Failed to load team dashboard."); }
    finally { setLoading(false); }
  };

  const statusColor = (s) => ({ ready: "success", in_progress: "warning", not_ready: "default", not_started: "default", certified: "success" }[s] || "default");
  const statusLabel = (s) => s?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "—";

  // Get all unique paths across all agents
  const allPaths = dashboard ? [...new Map(
    dashboard.agents.flatMap(a => (a.paths || []).map(p => [p.path_id, p.path_name]))
  ).entries()].map(([id, name]) => ({ path_id: id, path_name: name })) : [];

  const filteredAgents = (dashboard?.agents || []).filter(a => {
    const matchSearch = !search || a.full_name.toLowerCase().includes(search.toLowerCase()) || a.email.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || a.readiness_status === filterStatus;
    const matchPath = filterPath === "all" || (a.paths || []).some(p => p.path_id === filterPath);
    return matchSearch && matchStatus && matchPath;
  });

  const exportExcel = () => {
    const rows = [];
    filteredAgents.forEach(a => {
      if ((a.paths || []).length === 0) {
        rows.push({
          "Full Name": a.full_name,
          "Email": a.email,
          "Overall Status": statusLabel(a.readiness_status),
          "Learning Path": "",
          "Path Status": "",
          "Modules Completed": a.modules_completed,
          "Modules Total": a.modules_total,
          "Overall %": a.overall_percent,
          "Avg Score": a.average_score || "",
          "Last Activity": a.last_activity_at ? new Date(a.last_activity_at).toLocaleDateString() : "",
        });
      } else {
        (a.paths || []).forEach(p => {
          rows.push({
            "Full Name": a.full_name,
            "Email": a.email,
            "Overall Status": statusLabel(a.readiness_status),
            "Learning Path": p.path_name,
            "Path Status": statusLabel(p.status),
            "Modules Completed": p.modules_completed,
            "Modules Total": p.modules_total,
            "Overall %": p.overall_percent,
            "Avg Score": a.average_score || "",
            "Last Activity": a.last_activity_at ? new Date(a.last_activity_at).toLocaleDateString() : "",
          });
        });
      }
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Team Progress");
    XLSX.writeFile(wb, `${dashboard?.team_name || "team"}_progress.xlsx`);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fa" }}>
      <AppBar position="static" sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
        <Toolbar>
          <img src="/alteram-logo.png" alt="Alteram" style={{ height: 36, objectFit: "contain", marginRight: 8, backgroundColor: "white", padding: "3px 6px", borderRadius: 4 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>Synaptix — Team Dashboard</Typography>
          <Typography variant="body2" sx={{ mr: 2, opacity: 0.9 }}>{user?.full_name} — Supervisor</Typography>
          <Button color="inherit" onClick={() => navigate('/change-password')} startIcon={<LockIcon />} sx={{ mr: 1 }}>Change Password</Button>
          <Button color="inherit" onClick={logout} startIcon={<LogoutIcon />}>Sign Out</Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 4, maxWidth: 1400, mx: "auto" }}>
        <Typography variant="h4" fontWeight={700} color="#1a237e" mb={0.5}>Team Training Dashboard</Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>Monitor your team's training progress and certification status</Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress size={48} /></Box>
        ) : dashboard && (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {[
                { label: "Total Agents", value: dashboard.total_agents, icon: <GroupIcon />, color: "#1a237e" },
                { label: "Not Started", value: dashboard.not_started, icon: <TrendingUpIcon />, color: "#9e9e9e" },
                { label: "In Progress", value: dashboard.in_progress, icon: <TrendingUpIcon />, color: "#f57c00" },
                { label: "Ready for Final", value: dashboard.ready_for_final, icon: <CheckCircleIcon />, color: "#1565c0" },
                { label: "Certified", value: dashboard.completed, icon: <CheckCircleIcon />, color: "#2e7d32" },
              ].map((stat, i) => (
                <Grid item xs={6} md={2.4} key={i}>
                  <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                    <CardContent sx={{ textAlign: "center", p: 2 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: "50%", mx: "auto", mb: 1, bgcolor: `${stat.color}22`, display: "flex", alignItems: "center", justifyContent: "center", color: stat.color }}>
                        {stat.icon}
                      </Box>
                      <Typography variant="h3" fontWeight={800} color={stat.color}>{stat.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Filters */}
            <Card sx={{ borderRadius: 3, boxShadow: 1, mb: 3 }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                  <TextField placeholder="Search agent..." value={search} onChange={e => setSearch(e.target.value)}
                    size="small" sx={{ minWidth: 220 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Learning Path</InputLabel>
                    <Select value={filterPath} label="Learning Path" onChange={e => setFilterPath(e.target.value)}>
                      <MenuItem value="all">All Paths</MenuItem>
                      {allPaths.map(p => <MenuItem key={p.path_id} value={p.path_id}>{p.path_name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="not_started">Not Started</MenuItem>
                      <MenuItem value="not_ready">Not Ready</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="ready">Ready for Final</MenuItem>
                      <MenuItem value="certified">Certified</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                    {filteredAgents.length} of {dashboard.agents.length} agents
                  </Typography>
                  <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportExcel} disabled={filteredAgents.length === 0}>
                    Export Excel
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Agent Table */}
            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 0 }}>
                <Typography variant="h6" fontWeight={700} sx={{ p: 3, pb: 1 }}>
                  {dashboard.team_name} Team — Agent Progress
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                        <TableCell width={32} />
                        <TableCell><b>Agent</b></TableCell>
                        <TableCell><b>Overall Status</b></TableCell>
                        <TableCell><b>Progress</b></TableCell>
                        <TableCell><b>Avg Score</b></TableCell>
                        <TableCell><b>Last Activity</b></TableCell>
                        <TableCell><b>Learning Paths</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAgents.map((agent) => (
                        <>
                          <TableRow key={agent.user_id} hover sx={{ cursor: "pointer" }}
                            onClick={() => setExpandedAgent(expandedAgent === agent.user_id ? null : agent.user_id)}>
                            <TableCell>
                              <IconButton size="small">
                                {expandedAgent === agent.user_id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: "#1a237e", fontSize: 12 }}>
                                  {agent.full_name.split(" ").map(n => n[0]).join("")}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>{agent.full_name}</Typography>
                                  <Typography variant="caption" color="text.secondary">{agent.email}</Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip label={statusLabel(agent.readiness_status)} color={statusColor(agent.readiness_status)} size="small" />
                            </TableCell>
                            <TableCell sx={{ minWidth: 160 }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="caption">{agent.modules_completed}/{agent.modules_total} modules</Typography>
                                <Typography variant="caption" fontWeight={600}>{agent.overall_percent}%</Typography>
                              </Box>
                              <LinearProgress variant="determinate" value={agent.overall_percent} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
                            </TableCell>
                            <TableCell>
                              {agent.average_score
                                ? <Chip label={`${agent.average_score}%`} color={agent.average_score >= 80 ? "success" : "warning"} size="small" />
                                : <Typography variant="body2" color="text.secondary">—</Typography>}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {agent.last_activity_at ? new Date(agent.last_activity_at).toLocaleDateString() : "No activity"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {(agent.paths || []).length} path(s) — click to expand
                              </Typography>
                            </TableCell>
                          </TableRow>
                          {/* Expanded per-path details */}
                          <TableRow key={`${agent.user_id}-expand`}>
                            <TableCell colSpan={7} sx={{ p: 0, borderBottom: expandedAgent === agent.user_id ? undefined : "none" }}>
                              <Collapse in={expandedAgent === agent.user_id} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2, bgcolor: "#f8f9ff", borderBottom: "1px solid #e0e0e0" }}>
                                  <Typography variant="body2" fontWeight={700} mb={1} color="#1a237e">Learning Path Details</Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell><b>Learning Path</b></TableCell>
                                        <TableCell><b>Status</b></TableCell>
                                        <TableCell><b>Modules</b></TableCell>
                                        <TableCell><b>Progress</b></TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(agent.paths || []).length === 0 ? (
                                        <TableRow><TableCell colSpan={4} sx={{ color: "text.secondary" }}>No learning paths enrolled</TableCell></TableRow>
                                      ) : (agent.paths || []).map(p => (
                                        <TableRow key={p.path_id}>
                                          <TableCell>{p.path_name}</TableCell>
                                          <TableCell><Chip label={statusLabel(p.status)} color={statusColor(p.status)} size="small" /></TableCell>
                                          <TableCell>{p.modules_completed}/{p.modules_total}</TableCell>
                                          <TableCell sx={{ minWidth: 120 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                              <LinearProgress variant="determinate" value={p.overall_percent} sx={{ flexGrow: 1, height: 5, borderRadius: 3 }} />
                                              <Typography variant="caption">{p.overall_percent}%</Typography>
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    </Box>
  );
}
